import { useEffect, useState } from 'react';
import { supabase, fullNameLastFirst } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { StatTile } from '../../components/StatTile';
import ProfileTabBar from '../../components/ProfileTabBar';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

const ROLES   = ['admin', 'manager', 'coordinator', 'nurse', 'staff'];
const MODULES = [
  { key: 'hh',      label: 'Home Health',  icon: '🏥', desc: 'Pipeline, schedule, visits, clinical, assessments' },
  { key: 'dme',     label: 'DME',          icon: '📦', desc: 'Orders, pipeline, delivery, CMN tracking' },
  { key: 'finance', label: 'Finance',      icon: '💰', desc: 'Revenue, payroll, claims tracker' },
  { key: 'hr',      label: 'HR & Caregivers', icon: '👥', desc: 'Caregiver profiles, staff records' },
  { key: 'quality', label: 'Quality Mgmt', icon: '🛡️', desc: 'QA surveys, complaints log, incident log, policies, reported compliance data' },
  { key: 'scheduling', label: 'Scheduling & EVV', icon: '🗓️', desc: 'Schedule (CAERA), Verification Desk, EVV Feed — the native scheduler replacing AxisCare' },
];
const PERMS   = ['can_view', 'can_edit', 'can_delete', 'can_export'];
const PERM_LABELS = { can_view: 'View', can_edit: 'Edit', can_delete: 'Delete', can_export: 'Export' };

const ROLE_META = {
  admin:       { color: 'var(--danger)',  badge: 'Full access — all modules' },
  manager:     { color: 'var(--copper)',  badge: 'Finance access — all modules' },
  coordinator: { color: 'var(--accent2)', badge: 'HH + HR modules' },
  nurse:       { color: 'var(--success)', badge: 'HH view & edit only' },
  staff:       { color: 'var(--text3)',   badge: 'HH view only' },
};

const roleColor = (role) => ({
  admin:       { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  manager:     { bg: 'var(--copper-bg)', color: 'var(--copper)' },
  coordinator: { bg: 'var(--accent-bg)', color: 'var(--accent2)' },
  nurse:       { bg: 'var(--success-bg)', color: 'var(--success)' },
  staff:       { bg: 'var(--bg2)', color: 'var(--text3)' },
}[role] || { bg: 'var(--bg2)', color: 'var(--text3)' });

export default function AccessControl() {
  const [tab, setTab] = useState('roles');
  return (
    <div>
      <ProfileTabBar
        tabs={[
          { key: 'roles',     label: 'Role Defaults' },
          { key: 'overrides', label: 'Individual Overrides' },
        ]}
        activeKey={tab}
        onSelect={setTab}
      />
      {tab === 'roles' ? <RoleDefaultsTab /> : <IndividualOverridesTab />}
    </div>
  );
}

// ── Tab 1: Role Defaults — what a role gets unless a user has an override ────
function RoleDefaultsTab() {
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { loadPerms(); }, []);

  const loadPerms = async () => {
    setLoading(true);
    const { data, error: err } = await supabase.from('permissions').select('*');
    if (err) { setError(err.message); setLoading(false); return; }
    // Convert flat array to nested object: perms[role][module] = { can_view, can_edit, ... }
    const map = {};
    (data || []).forEach(row => {
      if (!map[row.role]) map[row.role] = {};
      map[row.role][row.module] = row;
    });
    setPerms(map);
    setLoading(false);
  };

  const toggle = async (role, module, perm) => {
    if (role === 'admin') return; // Admin permissions are immutable
    const current = perms[role]?.[module];
    if (!current) return;

    const newVal = !current[perm];

    // Business rules: if toggling can_view off, turn off all others too
    const updates = { [perm]: newVal };
    if (perm === 'can_view' && !newVal) {
      updates.can_edit = false;
      updates.can_delete = false;
      updates.can_export = false;
    }
    // If toggling can_edit/delete/export on, ensure can_view is on
    if (['can_edit','can_delete','can_export'].includes(perm) && newVal) {
      updates.can_view = true;
    }

    const key = `${role}-${module}-${perm}`;
    setSaving(key);

    const { error: err } = await supabase
      .from('permissions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('role', role)
      .eq('module', module);

    if (err) {
      setError(`Failed to update: ${err.message}`);
      setSaving(null);
      return;
    }

    // Update local state
    const newRolePerms = {
      ...perms[role],
      [module]: { ...perms[role][module], ...updates },
    };
    setPerms(prev => ({ ...prev, [role]: newRolePerms }));

    // No profile write needed — useModules() reads this `permissions` table
    // live (via permissionDefaults in useAuth), so any user without an
    // individual modules override picks up this change on their next load.

    setSaving(null);
    setSaved(key);
    setTimeout(() => setSaved(null), 1500);
  };

  const Check = ({ role, module, perm }) => {
    const val = perms[role]?.[module]?.[perm] ?? false;
    const isImmutable = role === 'admin';
    const key = `${role}-${module}-${perm}`;
    const isSaving = saving === key;
    const isSaved  = saved === key;

    return (
      <button
        onClick={() => !isImmutable && toggle(role, module, perm)}
        disabled={isImmutable || !!saving}
        title={isImmutable ? 'Admin permissions are fixed' : `Toggle ${PERM_LABELS[perm]} for ${role} on ${module}`}
        style={{
          width: 28, height: 28, borderRadius: 6,
          border: `1.5px solid ${val ? (isImmutable ? 'var(--text3)' : 'var(--accent)') : 'var(--border2)'}`,
          background: isSaved ? 'var(--success)' : val ? (isImmutable ? 'var(--bg3)' : 'var(--accent)') : 'var(--card-bg)',
          cursor: isImmutable ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s', flexShrink: 0,
          opacity: isImmutable ? 0.5 : 1,
        }}
      >
        {isSaving ? (
          <div style={{ width: 10, height: 10, border: '1.5px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        ) : (val || isSaved) ? (
          <svg width="12" height="12" fill="none" stroke={isSaved ? '#fff' : '#fff'} strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
        ) : null}
      </button>
    );
  };

  return (
    <div>
      {/* Header info */}
      <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="16" height="16" fill="none" stroke="var(--danger)" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--danger)' }}>Admin only — Default Role Permissions</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>These are the default module permissions by role, applied live to any user without an individual override. Set overrides for a specific person in the "Individual Overrides" tab — that always takes priority. Admin permissions are fixed.</div>
        </div>
      </div>

      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      {/* Role summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(0,1fr))', gap: 8, marginBottom: 20 }}>
        {ROLES.map(role => {
          const meta = ROLE_META[role];
          const moduleCount = MODULES.filter(m => perms[role]?.[m.key]?.can_view).length;
          return (
            <StatTile
              key={role}
              label={<span style={{ textTransform: 'capitalize' }}>{role}</span>}
              value={moduleCount}
              suffix={`/${MODULES.length}`}
              dot={meta.color}
              sub={<>modules<div style={{ marginTop: 3, lineHeight: 1.4 }}>{meta.badge}</div></>}
            />
          );
        })}
      </div>

      {/* Permissions table */}
      {loading ? (
        <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Module Permissions by Role</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {PERMS.map(p => (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text3)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--accent)' }} />
                  {PERM_LABELS[p]}
                </div>
              ))}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg2)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, width: 220 }}>Module</th>
                  {ROLES.map(role => {
                    const meta = ROLE_META[role];
                    return (
                      <th key={role} style={{ padding: '10px 8px', textAlign: 'center', minWidth: 140 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: meta.color, textTransform: 'capitalize' }}>{role}</span>
                          <div style={{ display: 'flex', gap: 3 }}>
                            {PERMS.map(p => (
                              <span key={p} style={{ fontSize: 9, color: 'var(--text3)' }}>{PERM_LABELS[p][0]}</span>
                            ))}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {MODULES.map((mod, mi) => (
                  <tr key={mod.key} style={{ borderBottom: '0.5px solid var(--border)', background: mi % 2 === 0 ? 'var(--card-bg)' : 'var(--bg2)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{mod.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{mod.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{mod.desc}</div>
                        </div>
                      </div>
                    </td>
                    {ROLES.map(role => (
                      <td key={role} style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {PERMS.map(perm => (
                            <Check key={perm} role={role} module={mod.key} perm={perm} />
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '12px 16px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              { icon: '✓', color: 'var(--accent)', bg: 'var(--accent)', label: 'Enabled' },
              { icon: '',  color: 'var(--border2)', bg: 'var(--card-bg)', label: 'Disabled' },
              { icon: '✓', color: 'var(--text3)', bg: 'var(--bg3)', label: 'Fixed (admin)' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)' }}>
                <div style={{ width: 18, height: 18, borderRadius: 4, background: l.bg, border: `1.5px solid ${l.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {l.icon && <svg width="10" height="10" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                {l.label}
              </div>
            ))}
            <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
              V = View · E = Edit · D = Delete · X = Export
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ── Tab 2: Individual Overrides — per-user module access exceptions ──────────

function IndividualOverridesTab() {
  const { permissionDefaults } = useAuth();
  const defaultModulesForRole = (role) => {
    const defaults = permissionDefaults?.[role];
    if (!defaults) return ['hh'];
    return Object.keys(defaults).filter(mod => defaults[mod]?.can_view);
  };

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editModules, setEditModules] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('profiles')
      .select('id, full_name, email, role, modules')
      .order('full_name');
    setStaff(data || []);
    setLoading(false);
  };

  const toast = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const openEdit = (member) => {
    setEditing(member);
    setEditModules(member.modules || defaultModulesForRole(member.role));
    setError('');
  };

  const toggleModule = (mod) => {
    setEditModules(prev => {
      const has = prev.includes(mod);
      if (has && prev.length === 1) return prev;
      return has ? prev.filter(m => m !== mod) : [...prev, mod];
    });
  };

  const saveOverride = async () => {
    setSaving(true); setError('');
    const { error: err } = await supabase.from('profiles').update({ modules: editModules }).eq('id', editing.id);
    if (err) { setError(err.message); setSaving(false); return; }
    await load();
    setEditing(null);
    toast(`${editing.full_name}'s access updated`);
    setSaving(false);
  };

  const resetToRoleDefault = async () => {
    setSaving(true); setError('');
    const { error: err } = await supabase.from('profiles').update({ modules: null }).eq('id', editing.id);
    if (err) { setError(err.message); setSaving(false); return; }
    await load();
    setEditing(null);
    toast(`${editing.full_name} reverted to role default`);
    setSaving(false);
  };

  const columns = [
    {
      id: 'name', header: 'Name', size: 200,
      meta: { mobile: 'primary' },
      cell: info => {
        const member = info.row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
              {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
            </div>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{fullNameLastFirst(member.full_name)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'role', header: 'Role', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => {
        const rc = roleColor(info.getValue());
        return <span style={{ fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: rc.bg, color: rc.color, textTransform: 'capitalize' }}>{info.getValue()}</span>;
      },
    },
    {
      id: 'source', header: 'Access Source', size: 130,
      meta: { mobile: 'primary' },
      cell: info => {
        const isCustom = info.row.original.modules != null;
        return (
          <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: isCustom ? 'var(--accent-bg)' : 'var(--bg2)', color: isCustom ? 'var(--accent2)' : 'var(--text3)' }}>
            {isCustom ? 'Custom' : 'Role Default'}
          </span>
        );
      },
    },
    {
      id: 'modules', header: 'Modules', size: 220, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const member = info.row.original;
        const mods = member.modules || defaultModulesForRole(member.role);
        return (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {mods.map(m => {
              const mod = MODULES.find(x => x.key === m);
              return mod ? <span key={m} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg2)', color: 'var(--text3)', border: '0.5px solid var(--border)' }}>{mod.icon} {mod.label}</span> : null;
            })}
          </div>
        );
      },
    },
    {
      id: 'actions', header: 'Actions', size: 140, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => (
        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(info.row.original)}>Edit Access</button>
      ),
    },
  ];

  return (
    <div>
      {success && <div style={{ background:'var(--success-bg)', border:'0.5px solid var(--success-border)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--success)' }}>✓ {success}</div>}
      {error && !editing && (
        <div style={{ background:'var(--danger-bg)', border:'0.5px solid var(--danger-border)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--danger)' }}>⚠ {error}</div>
      )}

      <div style={{ background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: 'var(--accent2)' }}>
        Give a specific person access different from their role's default — e.g. a Coordinator who also needs Finance. This always takes priority over the Role Defaults tab.
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Individual Access Overrides ({staff.length})</div>
        </div>
        <DataTable
          tableId="access-overrides"
          columns={columns}
          data={staff}
          getRowId={s => s.id}
          loading={loading}
          emptyMessage="No staff accounts yet."
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      <Modal show={!!editing} onClose={() => setEditing(null)} title={`Individual Access — ${fullNameLastFirst(editing?.full_name)}`} error={error}
        footer={<>
          <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
          <button className="btn btn-outline" onClick={resetToRoleDefault} disabled={saving}>Reset to Role Default</button>
          <button className="btn btn-primary" onClick={saveOverride} disabled={saving}>{saving ? 'Saving...' : 'Save Override'}</button>
        </>}>
        <div className="form-hint" style={{ marginBottom: 12 }}>
          {editing?.modules != null
            ? 'This person currently has a custom override.'
            : `This person currently uses the ${editing?.role} role default.`} Adjusting checkboxes below sets a custom override for them specifically.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          {MODULES.map(mod => {
            const isOn = editModules.includes(mod.key);
            return (
              <button key={mod.key} type="button" onClick={() => toggleModule(mod.key)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, border:`1.5px solid ${isOn?'var(--accent)':'var(--border)'}`, background:isOn?'var(--accent-bg)':'var(--bg2)', cursor:'pointer', textAlign:'left', fontFamily:'inherit', transition:'all 0.15s' }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{mod.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, color:isOn?'var(--accent2)':'var(--text)' }}>{mod.label}</div>
                  <div style={{ fontSize:10, color:'var(--text3)', marginTop:1 }}>{mod.desc}</div>
                </div>
                <div style={{ width:18, height:18, borderRadius:4, border:`1.5px solid ${isOn?'var(--accent)':'var(--border2)'}`, background:isOn?'var(--accent)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {isOn && <svg width="10" height="10" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
